/**
 * Story transformation layer for backend-to-frontend response mapping.
 *
 * Difficulty and duration arrive as flat normalized fields (level_cefr,
 * length_bucket) — the sole source of truth (TRA-125). Only title, modal,
 * and creator remain nested structures.
 */

import type {
    StoryDto,
    StoryTitleDto,
    StoryModalDto,
    StoryCreatorDto,
    LengthBucket,
} from '../stories.service'

/**
 * Backend story response type (what the API actually returns).
 * This is a hybrid of flat fields and nested dicts.
 */
export type BackendStoryResponse = {
    story_id: string
    corpus_id: string
    target_lang?: string | null
    user_lang?: string | null
    // Flat fields (always populated)
    title_canonical: string
    title_alternates?: string[]
    level_cefr: string | null
    length_bucket: string
    work_type: string
    tags: string[]
    // Nested dicts (may be empty or partial)
    creator?: Record<string, unknown>
    modal?: Record<string, unknown>
    attribution?: Record<string, unknown>
    rights?: Record<string, unknown>
    source?: Record<string, unknown>
    provenance?: Record<string, unknown>
    // Version info
    published_revision?: number
    version_id?: string
}

/**
 * Transform a backend story response to the frontend StoryDto format.
 *
 * This function merges flat fields with nested dicts, giving priority to flat fields
 * which are guaranteed to be populated by the backend.
 *
 * @param backend - The raw backend response
 * @returns A StoryDto with fully populated nested structures
 */
export function transformStoryResponse(backend: BackendStoryResponse): StoryDto {
    return {
        story_id: backend.story_id,
        corpus_id: backend.corpus_id,
        target_lang: backend.target_lang ?? undefined,
        title: buildTitle(backend),
        level_cefr: backend.level_cefr ?? null,
        length_bucket: backend.length_bucket as LengthBucket,
        work_type: backend.work_type,
        modal: buildModal(backend),
        creator: buildCreator(backend),
        attribution: buildAttribution(backend),
        rights: buildRights(backend),
        source: buildSource(backend),
        provenance: buildProvenance(backend),
        tags: backend.tags || [],
    }
}

/**
 * Build the nested title structure from flat fields and existing nested data.
 */
function buildTitle(backend: BackendStoryResponse): StoryTitleDto {
    const existingTitle =
        backend.title_alternates !== undefined ? { alternates: backend.title_alternates } : {}

    return {
        canonical: backend.title_canonical || '',
        alternates: backend.title_alternates || [],
        ...existingTitle,
    }
}

/**
 * Build the nested modal structure from existing nested data.
 * Modal data is typically stored in the snapshot, not as flat fields.
 */
function buildModal(backend: BackendStoryResponse): StoryModalDto {
    const existingModal = (backend.modal || {}) as Partial<StoryModalDto>

    return {
        summary: existingModal.summary || '',
        why_chosen: existingModal.why_chosen,
        learning_focus: existingModal.learning_focus,
        content_warnings: existingModal.content_warnings,
    }
}

/**
 * Build the nested creator structure from existing nested data.
 * Creator data is stored as a JSONB column.
 */
function buildCreator(backend: BackendStoryResponse): StoryCreatorDto {
    const existingCreator = (backend.creator || {}) as Partial<StoryCreatorDto>

    return {
        author: existingCreator.author,
        translator: existingCreator.translator,
        is_unglossio_original: existingCreator.is_unglossio_original ?? false,
    }
}

/**
 * Build the attribution structure from existing nested data.
 */
function buildAttribution(
    backend: BackendStoryResponse
): { text?: string; url?: string } | undefined {
    const existing = backend.attribution as { text?: string; url?: string } | undefined
    if (!existing || Object.keys(existing).length === 0) {
        return undefined
    }
    return existing
}

/**
 * Build the rights structure from existing nested data.
 */
function buildRights(
    backend: BackendStoryResponse
): { license?: string; copyright?: string } | undefined {
    const existing = backend.rights as { license?: string; copyright?: string } | undefined
    if (!existing || Object.keys(existing).length === 0) {
        return undefined
    }
    return existing
}

/**
 * Build the source structure from existing nested data.
 */
function buildSource(
    backend: BackendStoryResponse
): { url?: string; title?: string; publication_date?: string } | undefined {
    const existing = backend.source as
        { url?: string; title?: string; publication_date?: string } | undefined
    if (!existing || Object.keys(existing).length === 0) {
        return undefined
    }
    return existing
}

/**
 * Build the provenance structure from existing nested data.
 */
function buildProvenance(
    backend: BackendStoryResponse
): { generated_by?: string; generated_at?: string; model_version?: string } | undefined {
    const existing = backend.provenance as
        { generated_by?: string; generated_at?: string; model_version?: string } | undefined
    if (!existing || Object.keys(existing).length === 0) {
        return undefined
    }
    return existing
}

/**
 * Transform an array of backend story responses.
 */
export function transformStoryResponses(backends: BackendStoryResponse[]): StoryDto[] {
    return backends.map(transformStoryResponse)
}
